import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Truck, Percent, Ticket, Plus, Trash2, Save } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { getFounderFormules, putFounderFormules, partnerGetMenu } from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };
const TYPES = [["percent", "Réduction −%"], ["free_delivery", "Livraison offerte"], ["bogo_menu", "1 acheté = 1 offert"]];

export default function FounderFormules() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getFounderFormules(), partnerGetMenu().catch(() => null)])
      .then(([c, menu]) => {
        setCfg(c);
        const all = [];
        (menu?.categories || []).forEach((cat) => (cat.items || []).forEach((it) => all.push({ id: it.id, name: it.name, cat: cat.name })));
        setItems(all);
      })
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  if (!isAdmin) return <div className="max-w-2xl mx-auto px-4 py-16 text-center" style={{ color: "#64748b" }} data-testid="ff-denied">Réservé au Founder.</div>;
  if (loading || !cfg) return <div className="max-w-3xl mx-auto px-4 py-10"><div className="h-40 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} data-testid="ff-loading" /></div>;

  const setFd = (patch) => setCfg((c) => ({ ...c, free_delivery: { ...c.free_delivery, ...patch } }));
  const setSh = (patch) => setCfg((c) => ({ ...c, second_half: { ...c.second_half, ...patch } }));
  const toggleItem = (key, id) => setCfg((c) => {
    const cur = new Set(c[key].item_ids || []);
    cur.has(id) ? cur.delete(id) : cur.add(id);
    return { ...c, [key]: { ...c[key], item_ids: [...cur] } };
  });
  const setCode = (i, patch) => setCfg((c) => ({ ...c, promo_codes: c.promo_codes.map((x, j) => j === i ? { ...x, ...patch } : x) }));
  const addCode = () => setCfg((c) => ({ ...c, promo_codes: [...(c.promo_codes || []), { code: "", type: "percent", value: 10, item_ids: [], enabled: true }] }));
  const delCode = (i) => setCfg((c) => ({ ...c, promo_codes: c.promo_codes.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try { const saved = await putFounderFormules(cfg); setCfg(saved); toast.success("Formules enregistrées"); }
    catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  const bogoCode = cfg.promo_codes.some((c) => c.type === "bogo_menu");

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="founder-formules">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold" style={{ color: "#0b1220" }}>Formules commerciales La BASE</h1>
        <button data-testid="ff-save" onClick={save} disabled={busy}
          className="flex items-center gap-1.5 text-sm font-bold px-4 h-10 rounded-xl text-white" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>
          <Save size={16} /> {busy ? "…" : "Enregistrer"}
        </button>
      </div>

      {/* Livraison offerte */}
      <section className="rounded-2xl bg-white p-5 mb-4" style={{ border: "1px solid #eef0ef" }} data-testid="ff-freedelivery">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="flex items-center gap-2 font-bold" style={{ color: GREEN }}><Truck size={18} /> Livraison offerte</span>
          <input type="checkbox" data-testid="ff-fd-enabled" checked={!!cfg.free_delivery.enabled} onChange={(e) => setFd({ enabled: e.target.checked })} />
        </label>
        {cfg.free_delivery.enabled && (
          <div className="mt-3 flex items-center gap-2 text-sm" style={{ color: "#475569" }}>
            <span>Dès</span>
            <input data-testid="ff-fd-threshold" inputMode="decimal" value={cfg.free_delivery.threshold_chf ?? ""} onChange={(e) => setFd({ threshold_chf: e.target.value })}
              placeholder="toujours" className="w-28 rounded-xl px-3 h-10 outline-none" style={field} />
            <span>CHF (vide = toujours offerte)</span>
          </div>
        )}
      </section>

      {/* 2e à -50% */}
      <section className="rounded-2xl bg-white p-5 mb-4" style={{ border: "1px solid #eef0ef" }} data-testid="ff-secondhalf">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="flex items-center gap-2 font-bold" style={{ color: ORANGE }}><Percent size={18} /> 2e article à -50 %</span>
          <input type="checkbox" data-testid="ff-sh-enabled" checked={!!cfg.second_half.enabled} onChange={(e) => setSh({ enabled: e.target.checked })} />
        </label>
        {cfg.second_half.enabled && (
          <div className="mt-3">
            <p className="text-xs mb-2" style={{ color: "#94a3b8" }}>Sélectionnez les articles éligibles ({(cfg.second_half.item_ids || []).length} choisi(s))</p>
            <div className="max-h-56 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {items.map((it) => (
                <label key={it.id} data-testid={`ff-sh-item-${it.id}`} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 cursor-pointer" style={field}>
                  <input type="checkbox" checked={(cfg.second_half.item_ids || []).includes(it.id)} onChange={() => toggleItem("second_half", it.id)} />
                  <span className="truncate">{it.name} <span style={{ color: "#94a3b8" }}>· {it.cat}</span></span>
                </label>
              ))}
              {items.length === 0 && <p className="text-sm" style={{ color: "#94a3b8" }}>Aucun article dans le catalogue Maison.</p>}
            </div>
          </div>
        )}
      </section>

      {/* Codes promo */}
      <section className="rounded-2xl bg-white p-5" style={{ border: "1px solid #eef0ef" }} data-testid="ff-codes">
        <div className="flex items-center justify-between mb-3">
          <span className="flex items-center gap-2 font-bold" style={{ color: GREEN }}><Ticket size={18} /> Codes promo</span>
          <button data-testid="ff-code-add" onClick={addCode} className="flex items-center gap-1 text-sm font-bold px-3 h-9 rounded-xl" style={field}><Plus size={15} /> Ajouter</button>
        </div>
        <div className="space-y-3">
          {(cfg.promo_codes || []).map((c, i) => (
            <div key={i} data-testid={`ff-code-${i}`} className="rounded-xl p-3" style={field}>
              <div className="flex items-center gap-2 flex-wrap">
                <input data-testid={`ff-code-value-${i}`} value={c.code} onChange={(e) => setCode(i, { code: e.target.value.toUpperCase() })} placeholder="CODE" className="w-28 rounded-lg px-3 h-10 text-sm font-bold outline-none bg-white" style={{ border: "1px solid #e5e7eb" }} />
                <select data-testid={`ff-code-type-${i}`} value={c.type} onChange={(e) => setCode(i, { type: e.target.value })} className="rounded-lg px-2 h-10 text-sm outline-none bg-white" style={{ border: "1px solid #e5e7eb" }}>
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {c.type === "percent" && (
                  <input data-testid={`ff-code-pct-${i}`} inputMode="decimal" value={c.value} onChange={(e) => setCode(i, { value: e.target.value })} placeholder="%" className="w-16 rounded-lg px-2 h-10 text-sm outline-none bg-white" style={{ border: "1px solid #e5e7eb" }} />
                )}
                <label className="flex items-center gap-1 text-xs ml-auto" style={{ color: "#475569" }}>
                  <input type="checkbox" checked={!!c.enabled} onChange={(e) => setCode(i, { enabled: e.target.checked })} /> actif
                </label>
                <button data-testid={`ff-code-del-${i}`} onClick={() => delCode(i)} style={{ color: "#dc2626" }}><Trash2 size={16} /></button>
              </div>
              {c.type === "bogo_menu" && (
                <div className="mt-2 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {items.map((it) => (
                    <label key={it.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1 cursor-pointer bg-white" style={{ border: "1px solid #e5e7eb" }}>
                      <input type="checkbox" checked={(c.item_ids || []).includes(it.id)} onChange={() => setCode(i, { item_ids: (c.item_ids || []).includes(it.id) ? c.item_ids.filter((x) => x !== it.id) : [...(c.item_ids || []), it.id] })} />
                      <span className="truncate">{it.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
          {(cfg.promo_codes || []).length === 0 && <p className="text-sm" style={{ color: "#94a3b8" }}>Aucun code. Cliquez sur « Ajouter ».</p>}
        </div>
        {bogoCode && items.length === 0 && <p className="text-xs mt-2" style={{ color: "#f59e0b" }}>Ajoutez d&apos;abord des articles au catalogue pour configurer un code « 1 acheté = 1 offert ».</p>}
      </section>
    </div>
  );
}
